export function PrivacyPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
      <p className="text-gray-600 mb-8">Last updated: March 17, 2026</p>

      <div className="prose max-w-none">
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">1. Information We Collect</h2>
          <p className="text-gray-700 mb-4">We collect information you provide directly to us, such as when you create an account, make a purchase, or contact us for support.</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Personal information (name, email address, phone number)</li>
            <li>Account credentials and profile information</li>
            <li>Payment and billing information</li>
            <li>Communications with our support team</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">2. How We Use Your Information</h2>
          <p className="text-gray-700 mb-4">We use the information we collect to:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Provide, maintain, and improve our services</li>
            <li>Process transactions and send related information</li>
            <li>Send technical notices and support messages</li>
            <li>Respond to your comments and questions</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">3. Information Sharing</h2>
          <p className="text-gray-700 mb-4">We do not sell, trade, or otherwise transfer your personal information to third parties except as described in this policy.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">4. Data Security</h2>
          <p className="text-gray-700 mb-4">We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">5. Your Rights</h2>
          <p className="text-gray-700 mb-4">You have the right to access, update, or delete your personal information. Contact us to exercise these rights.</p>
          
          <h3 className="text-xl font-semibold mb-3 mt-6">For EU/EEA Users (GDPR)</h3>
          <p className="text-gray-700 mb-2">If you are located in the European Union or European Economic Area, you have additional rights under the General Data Protection Regulation (GDPR):</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
            <li><strong>Legal Basis for Processing:</strong> We process your data based on: (a) your consent when creating an account, (b) contractual necessity to provide the Service, and (c) legitimate interest for service improvement and security</li>
            <li><strong>Right to Data Portability:</strong> You may request a copy of your data in a structured, machine-readable format</li>
            <li><strong>Right to Restriction:</strong> You may request we restrict processing of your data in certain circumstances</li>
            <li><strong>Right to Object:</strong> You may object to processing based on legitimate interest</li>
            <li><strong>Supervisory Authority:</strong> You have the right to lodge a complaint with your local data protection authority</li>
          </ul>
          
          <h3 className="text-xl font-semibold mb-3 mt-6">For Indian Users (DPDP Act 2023)</h3>
          <p className="text-gray-700 mb-2">Under India's Digital Personal Data Protection Act, 2023:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
            <li>You are a "Data Principal" and CloudNestle is a "Data Fiduciary"</li>
            <li>You have the right to access, correct, and erase your personal data</li>
            <li>You have the right to nominate another person to exercise your rights</li>
            <li>You may withdraw consent at any time by contacting <a href="mailto:privacy@cloudnestle.com" className="text-blue-600 hover:underline">privacy@cloudnestle.com</a></li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">6. Retention of Personal Information</h2>
          <p className="text-gray-700 mb-4">We keep your personal information to enable your continued use of our Offerings, for as long as it is required in order to fulfill the relevant purposes described in this Privacy Notice, as may be required by law (including for tax and accounting purposes), or as otherwise communicated to you. How long we retain specific personal information varies depending on the purpose for its use, and we will delete your personal information in accordance with applicable law.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">7. Third-Party Data Processors</h2>
          <p className="text-gray-700 mb-4">We use trusted third-party service providers to operate our marketplace. These processors only access your data to perform specific tasks on our behalf and are obligated to protect your information.</p>
          
          <h3 className="text-xl font-semibold mb-3 mt-6">Infrastructure & Hosting</h3>
          <div className="mb-4">
            <p className="font-semibold text-gray-900">Amazon Web Services (AWS)</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
              <li><strong>Purpose:</strong> Data storage, application hosting, and computing services</li>
              <li><strong>Data Location:</strong> US East (Virginia) region</li>
              <li><strong>Compliance:</strong> GDPR compliant, ISO 27001, SOC 2</li>
              <li><strong>DPA:</strong> AWS Data Processing Agreement automatically applies to all customers</li>
              <li><strong>Learn more:</strong> <a href="https://aws.amazon.com/compliance/gdpr-center/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://aws.amazon.com/compliance/gdpr-center/</a></li>
            </ul>
          </div>
          
          <div className="mb-4">
            <p className="font-semibold text-gray-900">Amazon CloudFront</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
              <li><strong>Purpose:</strong> Content delivery and geo-location services</li>
              <li><strong>Compliance:</strong> Part of AWS GDPR compliance framework</li>
            </ul>
          </div>
          
          <div className="mb-4">
            <p className="font-semibold text-gray-900">Amazon Cognito</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
              <li><strong>Purpose:</strong> User authentication, account management, and session handling</li>
              <li><strong>Data Processed:</strong> Email address, password (hashed), login history</li>
              <li><strong>Compliance:</strong> Part of AWS GDPR compliance framework</li>
            </ul>
          </div>
          
          <div className="mb-4">
            <p className="font-semibold text-gray-900">Amazon DynamoDB</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
              <li><strong>Purpose:</strong> Storage of user accounts, subscriptions, entitlements, and payment records</li>
              <li><strong>Data Location:</strong> US East (Virginia) region</li>
              <li><strong>Compliance:</strong> Encrypted at rest, part of AWS GDPR compliance framework</li>
            </ul>
          </div>
          
          <div className="mb-4">
            <p className="font-semibold text-gray-900">Amazon Bedrock (Nova Pro)</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
              <li><strong>Purpose:</strong> AI-powered search recommendations and natural language processing of your search queries</li>
              <li><strong>Data Processed:</strong> Search queries you submit to the Service</li>
              <li><strong>Data Retention:</strong> Queries are not stored by Amazon Bedrock after processing</li>
              <li><strong>Compliance:</strong> Part of AWS GDPR compliance framework</li>
            </ul>
          </div>
          
          <h3 className="text-xl font-semibold mb-3 mt-6">Payment Processing</h3>
          <div className="mb-4">
            <p className="font-semibold text-gray-900">Cashfree Payments</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
              <li><strong>Purpose:</strong> Payment processing for Indian transactions</li>
              <li><strong>Compliance:</strong> PCI-DSS Level 1 certified, RBI compliant</li>
              <li><strong>Data Processed:</strong> Billing information, payment details, GSTIN</li>
              <li><strong>Privacy Policy:</strong> <a href="https://www.cashfree.com/privacy-policy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://www.cashfree.com/privacy-policy</a></li>
            </ul>
          </div>
          
          <div className="mb-4">
            <p className="font-semibold text-gray-900">PayU</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
              <li><strong>Purpose:</strong> Payment processing for international transactions</li>
              <li><strong>Compliance:</strong> PCI-DSS compliant</li>
              <li><strong>Data Processed:</strong> Billing information, payment details</li>
              <li><strong>Privacy Policy:</strong> <a href="https://payu.in/privacy-policy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://payu.in/privacy-policy</a></li>
            </ul>
          </div>
          
          <h3 className="text-xl font-semibold mb-3 mt-6">Data Processing Safeguards</h3>
          <p className="text-gray-700 mb-2">All our third-party processors:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1">
            <li>Are contractually bound to data protection standards (GDPR, CCPA, DPDP Act)</li>
            <li>Implement appropriate technical and organizational security measures</li>
            <li>Process data only according to our documented instructions</li>
            <li>Do not use your personal data for their own purposes</li>
            <li>Maintain confidentiality of all processed data</li>
            <li>Assist us in responding to data subject requests</li>
          </ul>
          
          <h3 className="text-xl font-semibold mb-3 mt-6">Data Transfers</h3>
          <p className="text-gray-700 mb-4">Your personal data may be transferred to and processed in countries outside your country of residence, including the United States. We ensure that such transfers comply with applicable data protection laws through:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1">
            <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
            <li>Adequacy decisions where applicable</li>
            <li>Appropriate safeguards as required by law</li>
          </ul>
          
          <h3 className="text-xl font-semibold mb-3 mt-6">Request Data Processing Agreement</h3>
          <p className="text-gray-700 mb-2">If you would like to review our Data Processing Agreements with third-party processors, please contact us at:</p>
          <ul className="list-none text-gray-700 space-y-1 ml-4">
            <li><strong>Email:</strong> <a href="mailto:privacy@cloudnestle.com" className="text-blue-600 hover:underline">privacy@cloudnestle.com</a></li>
            <li><strong>Subject:</strong> Data Processing Agreement Request</li>
          </ul>
          <p className="text-gray-700 mt-2">We will provide the requested information within 30 days of your request.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">8. Data Retention</h2>
          <p className="text-gray-700 mb-4">We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.</p>
          
          <h3 className="text-xl font-semibold mb-3">Specific Retention Periods:</h3>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li><strong>Account Information:</strong> Retained while your account is active, plus 90 days after account closure</li>
            <li><strong>Payment Records:</strong> 7 years (as required by tax and accounting regulations)</li>
            <li><strong>Usage Logs:</strong> 12 months</li>
            <li><strong>Marketing Data:</strong> Until you withdraw consent or 2 years of inactivity</li>
            <li><strong>Support Communications:</strong> 3 years</li>
            <li><strong>Feedback Submissions:</strong> 2 years</li>
          </ul>
          
          <p className="text-gray-700 mt-4">After the retention period expires, we securely delete or anonymize your personal data.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">9. Cookies and Local Storage</h2>
          <p className="text-gray-700 mb-4">Our website uses browser local storage (not cookies) to enhance your experience and maintain your session. Local storage is similar to cookies but stays only in your browser.</p>
          
          <h3 className="text-xl font-semibold mb-3">What We Store Locally</h3>
          <p className="text-gray-700 mb-2"><strong>Essential Storage (Required for Service):</strong></p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
            <li><strong>Authentication tokens</strong> - To keep you logged in securely</li>
            <li><strong>Session data</strong> - To maintain your active session</li>
            <li><strong>User preferences</strong> - To remember your settings and choices</li>
          </ul>
          <p className="text-gray-700 mb-4">These are essential for the website to function and cannot be disabled without affecting your ability to use our services.</p>
          
          <h3 className="text-xl font-semibold mb-3">Third-Party Storage</h3>
          <p className="text-gray-700 mb-4">Our payment processors (Cashfree, PayU) may use their own cookies during checkout. Please refer to their privacy policies for details.</p>
          
          <h3 className="text-xl font-semibold mb-3">Managing Local Storage</h3>
          <p className="text-gray-700 mb-2">You can clear local storage at any time through your browser settings:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
            <li><strong>Chrome:</strong> Settings → Privacy and security → Clear browsing data</li>
            <li><strong>Firefox:</strong> Settings → Privacy & Security → Clear Data</li>
            <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
            <li><strong>Edge:</strong> Settings → Privacy → Clear browsing data</li>
          </ul>
          <p className="text-gray-700 mb-4"><em>Note: Clearing local storage will log you out and reset your preferences.</em></p>
          
          <h3 className="text-xl font-semibold mb-3">No Tracking Cookies</h3>
          <p className="text-gray-700 mb-2">We do NOT use:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
            <li>Advertising cookies or pixels</li>
            <li>Third-party tracking cookies</li>
            <li>Analytics cookies (beyond basic CloudFront access logs)</li>
            <li>Social media tracking cookies</li>
          </ul>
          
          <h3 className="text-xl font-semibold mb-3">Campaign Attribution</h3>
          <p className="text-gray-700 mb-2">We use URL parameters (UTM tags) to understand how users discover our Service. When you register, the following may be recorded:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1">
            <li><strong>utm_source:</strong> Where you came from (e.g., LinkedIn, direct)</li>
            <li><strong>utm_medium:</strong> The type of link (e.g., beta, pricing page)</li>
            <li><strong>utm_campaign:</strong> The specific campaign (e.g., free-trial)</li>
          </ul>
          <p className="text-gray-700 mt-2">This data is stored with your account for internal analytics only and is not shared with third parties.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">10. Refund and Cancellation Policy</h2>
          
          <h3 className="text-xl font-semibold mb-3">Digital Products and Services</h3>
          <p className="text-gray-700 mb-4">Due to the nature of digital products and services, all sales are final. Once you have been granted access to a solution or service, we cannot offer refunds.</p>
          
          <p className="text-gray-700 mb-2"><strong>Why No Refunds:</strong></p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
            <li>Digital products are delivered instantly upon purchase</li>
            <li>You gain immediate access to the full service/solution</li>
            <li>Free trials are available for most solutions before purchase</li>
            <li>Detailed product descriptions and features are provided before purchase</li>
          </ul>
          
          <h3 className="text-xl font-semibold mb-3">Exceptions</h3>
          <p className="text-gray-700 mb-2">We may provide refunds in the following exceptional circumstances:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
            <li><strong>Technical Issues:</strong> If we are unable to provide access to the purchased solution due to technical problems on our end within 7 days of purchase</li>
            <li><strong>Duplicate Charges:</strong> If you were charged multiple times for the same purchase due to a payment processing error</li>
            <li><strong>Unauthorized Transactions:</strong> If your account was compromised and purchases were made without your authorization (subject to verification)</li>
          </ul>
          
          <h3 className="text-xl font-semibold mb-3">Subscription Cancellations</h3>
          <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
            <li>Contact support@cloudnestle.com to cancel your subscription</li>
            <li>Cancellations are processed within 2 business days</li>
            <li>Access continues until the end of your current billing period</li>
            <li>No refunds for unused time in the current period</li>
          </ul>
          
          <h3 className="text-xl font-semibold mb-3">How to Request a Refund</h3>
          <p className="text-gray-700 mb-2">If you believe you qualify for a refund under the exceptions listed above:</p>
          <ol className="list-decimal pl-6 text-gray-700 space-y-1 mb-4">
            <li>Contact us at support@cloudnestle.com within 7 days of purchase</li>
            <li>Include your order ID, purchase date, and reason for refund request</li>
            <li>Provide any supporting documentation (screenshots, error messages, etc.)</li>
            <li>We will review your request and respond within 5 business days</li>
          </ol>
          
          <h3 className="text-xl font-semibold mb-3">Refund Processing</h3>
          <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
            <li>Approved refunds will be processed within 10 business days</li>
            <li>Refunds will be issued to the original payment method</li>
            <li>Processing time may vary depending on your bank or payment provider</li>
          </ul>
          
          <h3 className="text-xl font-semibold mb-3">Chargebacks</h3>
          <p className="text-gray-700 mb-2">If you file a chargeback or payment dispute with your bank or payment provider:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-4">
            <li>Your account will be immediately suspended pending investigation</li>
            <li>You will lose access to all purchased solutions and services</li>
            <li>We reserve the right to pursue legal action for fraudulent chargebacks</li>
            <li>Please contact us first to resolve any billing issues</li>
          </ul>
          
          <h3 className="text-xl font-semibold mb-3">Consumer Protection</h3>
          <p className="text-gray-700 mb-4">For customers in India, this policy is subject to the Consumer Protection Act, 2019. For international customers, this policy is subject to applicable consumer protection laws in your jurisdiction. Nothing in this policy limits your statutory rights.</p>
          
          <h3 className="text-xl font-semibold mb-3">Contact for Disputes</h3>
          <p className="text-gray-700 mb-2">If you have concerns about a purchase or this refund policy:</p>
          <ul className="list-none text-gray-700 space-y-1 ml-4">
            <li><strong>Email:</strong> support@cloudnestle.com</li>
            <li><strong>Grievance Officer:</strong> grievanceofficer@cloudnestle.com</li>
            <li><strong>Response Time:</strong> Within 5 business days</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">11. Contact Us</h2>
          <p className="text-gray-700">If you have questions about this Privacy Policy, please contact us at support@cloudnestle.com</p>
        </section>

        <section className="bg-gray-50 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Company Information</h2>
          <div className="space-y-3 text-sm text-gray-700">
            <p className="font-semibold text-gray-900">CloudNestle Consulting & Services</p>
            
            <div>
              <p className="font-semibold text-gray-900 mb-1">🇺🇸 US Sales & Support:</p>
              <p>Sales: <a href="mailto:sales@cloudnestle.com" className="text-blue-600 hover:text-blue-800">sales@cloudnestle.com</a></p>
              <p>Support: <a href="mailto:support@cloudnestle.com" className="text-blue-600 hover:text-blue-800">support@cloudnestle.com</a></p>
            </div>
            
            <div>
              <p className="font-semibold text-gray-900 mb-1">📍 Global Headquarters:</p>
              <p className="leading-relaxed">
                🇮🇳 Ground floor, #85, 2nd Cross Road,<br />
                Central Excise Layout, Vijay Nagar,<br />
                Bangalore 560040, India
              </p>
            </div>
            
            <div>
              <p className="font-semibold text-gray-900">🏢 GSTIN: 29ADWPA6289Q1ZB</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
